import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import type { ApplicantExportRow } from '../utils/applicant-export.util';
import type {
  LeadOwnerSummary,
  LeadStatusSummary,
  LeadSubStatusSummary,
  WorkflowDocumentation,
} from './leads.service';

export type ApplicantSubStatusOption = {
  _id: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
};

export type ApplicantArchiveFilter = 'active' | 'archived' | 'all';

export type ApplicantDto = {
  _id: string;
  franchiseId?: string;
  leadId: string;
  statusId: string;
  status: LeadStatusSummary;
  subStatusId?: string;
  subStatus?: LeadSubStatusSummary;
  fullName: string;
  email?: string;
  phoneNumber: string;
  preferredDestination?: string;
  message?: string;
  latestNote?: string;
  sourcePage?: string;
  leadSource?: string;
  whatsappNumber?: string;
  city?: string;
  state?: string;
  country?: string;
  qualification?: string;
  percentageOrCgpa?: string;
  passingYear?: string;
  institution?: string;
  preferredCourse?: string;
  intake?: string;
  budgetRange?: string;
  passportStatus?: string;
  englishTestStatus?: string;
  loanRequired?: string;
  financeTotalAmount?: number;
  financePaidAmount?: number;
  financePendingAmount?: number;
  profilePhotoMediaAssetId?: string;
  profilePhotoUrl?: string;
  isArchived: boolean;
  archivedAt?: string;
  archivedByUserId?: string;
  updatedByUserId?: string;
  ownerUserId?: string;
  assignedAt?: string;
  assignedByUserId?: string;
  convertedAt?: string;
  convertedByUserId?: string;
  owner?: LeadOwnerSummary;
  createdAt?: string;
  updatedAt?: string;
};

export type PaginatedApplicants = {
  items: ApplicantDto[];
  total: number;
  page: number;
  pageSize: number;
};

export type ApplicantStatusOption = { _id: string; code: string; name: string };

export type ApplicantTransitionOption = {
  _id: string;
  transitionName: string;
  description?: string;
  requiresComment: boolean;
  toStatus: LeadStatusSummary;
};

export type ListApplicantsParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  statusId?: string;
  preferredDestination?: string;
  createdFrom?: string;
  createdTo?: string;
  archiveFilter?: ApplicantArchiveFilter;
  ownerUserId?: string;
  createdToday?: boolean;
  statusCodes?: string;
  pendingTasksAssignedToMe?: boolean;
  showDisqualified?: boolean;
};

export type UpdateApplicantPayload = {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  city?: string;
  state?: string;
  country?: string;
  qualification?: string;
  percentageOrCgpa?: string;
  passingYear?: string;
  institution?: string;
  preferredDestination?: string;
  preferredCourse?: string;
  intake?: string;
  budgetRange?: string;
  passportStatus?: string;
  englishTestStatus?: string;
  loanRequired?: string;
  leadSource?: string;
  message?: string;
  financeTotalAmount?: number | null;
  financePaidAmount?: number | null;
  financePendingAmount?: number | null;
  profilePhotoMediaAssetId?: string;
};

export type TransitionApplicantPayload = {
  targetStatusId: string;
  comment?: string;
};

export type ApplicantImportResult = {
  total: number;
  updated: number;
  created: number;
  leadsCreated: number;
  leadsUpdated: number;
  skipped: number;
  errors: { row: number; message: string }[];
};

function toQuery(params: ListApplicantsParams): string {
  const q = new URLSearchParams();
  if (params.page) q.set('page', String(params.page));
  if (params.pageSize) q.set('pageSize', String(params.pageSize));
  if (params.search?.trim()) q.set('search', params.search.trim());
  if (params.statusId) q.set('statusId', params.statusId);
  if (params.statusCodes?.trim()) q.set('statusCodes', params.statusCodes.trim());
  if (params.preferredDestination?.trim()) q.set('preferredDestination', params.preferredDestination.trim());
  if (params.createdFrom) q.set('createdFrom', params.createdFrom);
  if (params.createdTo) q.set('createdTo', params.createdTo);
  if (params.createdToday) q.set('createdToday', 'true');
  if (params.pendingTasksAssignedToMe) q.set('pendingTasksAssignedToMe', 'true');
  if (params.showDisqualified) q.set('showDisqualified', 'true');
  if (params.archiveFilter) q.set('archiveFilter', params.archiveFilter);
  if (params.ownerUserId) q.set('ownerUserId', params.ownerUserId);
  const s = q.toString();
  return s ? `?${s}` : '';
}

@Injectable({ providedIn: 'root' })
export class ApplicantsService {
  private readonly http = inject(HttpClient);

  constructor(private readonly api: ApiService) {}

  listPlatform(params: ListApplicantsParams = {}) {
    return this.api
      .get<ApiEnvelope<PaginatedApplicants>>(`platform/applicants${toQuery(params)}`)
      .pipe(map((r) => r.data));
  }

  exportPlatform(params: ListApplicantsParams = {}) {
    return this.api
      .get<ApiEnvelope<ApplicantExportRow[]>>(`platform/applicants/export${toQuery(params)}`)
      .pipe(map((r) => r.data));
  }

  importPlatform(rows: ApplicantExportRow[]) {
    return this.api
      .post<ApiEnvelope<ApplicantImportResult>>('platform/applicants/import', { rows })
      .pipe(map((r) => r.data));
  }

  listStatuses() {
    return this.api
      .get<ApiEnvelope<ApplicantStatusOption[]>>('platform/applicants/statuses')
      .pipe(map((r) => r.data));
  }

  getById(id: string) {
    return this.api.get<ApiEnvelope<ApplicantDto>>(`platform/applicants/${id}`).pipe(map((r) => r.data));
  }

  update(id: string, payload: UpdateApplicantPayload) {
    return this.api
      .patch<ApiEnvelope<ApplicantDto>>(`platform/applicants/${id}`, payload)
      .pipe(map((r) => r.data));
  }

  uploadProfilePhoto(id: string, file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.http
      .post<ApiEnvelope<ApplicantDto>>(`${this.api.baseUrl}/platform/applicants/${id}/profile-photo`, form, {
        withCredentials: true,
      })
      .pipe(map((r) => r.data));
  }

  archive(id: string) {
    return this.api
      .post<ApiEnvelope<ApplicantDto>>(`platform/applicants/${id}/archive`, {})
      .pipe(map((r) => r.data));
  }

  unarchive(id: string) {
    return this.api
      .post<ApiEnvelope<ApplicantDto>>(`platform/applicants/${id}/unarchive`, {})
      .pipe(map((r) => r.data));
  }

  getTransitions(id: string) {
    return this.api
      .get<ApiEnvelope<ApplicantTransitionOption[]>>(`platform/applicants/${id}/transitions`)
      .pipe(map((r) => r.data));
  }

  getWorkflowDocumentation() {
    return this.api
      .get<ApiEnvelope<WorkflowDocumentation>>('platform/applicants/workflow')
      .pipe(map((r) => r.data));
  }

  transition(id: string, payload: TransitionApplicantPayload) {
    return this.api
      .post<ApiEnvelope<ApplicantDto>>(`platform/applicants/${id}/transition`, payload)
      .pipe(map((r) => r.data));
  }

  listSubStatuses() {
    return this.api
      .get<ApiEnvelope<ApplicantSubStatusOption[]>>(`platform/applicants/sub-statuses`)
      .pipe(map((r) => r.data));
  }

  updateSubStatus(id: string, subStatusId: string | null) {
    return this.api
      .patch<ApiEnvelope<ApplicantDto>>(`platform/applicants/${id}/sub-status`, { subStatusId })
      .pipe(map((r) => r.data));
  }

  assignOwner(id: string, ownerUserId: string) {
    return this.api
      .post<ApiEnvelope<ApplicantDto>>(`platform/applicants/${id}/assign`, { ownerUserId })
      .pipe(map((r) => r.data));
  }
}
